import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuthorDetailPage() {
    const { id } = useParams();
    const [author, setAuthor] = useState(null);
    const [stories, setStories] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [authorRes, storiesRes] = await Promise.all([
                    axios.get(`${API}/authors/${id}`),
                    axios.get(`${API}/stories`)
                ]);
                setAuthor(authorRes.data);
                setStories(storiesRes.data.filter(s => s.author_id === id));
            } catch (error) {
                console.error('Failed to fetch author:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <p className="text-xl italic text-muted-foreground">Načítání...</p>
            </div>
        );
    }

    if (!author) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center">
                <p className="text-xl italic mb-4">Autor nenalezen</p>
                <Link to="/autori" className="italic hover:underline">
                    Zpět na seznam autorů
                </Link>
            </div>
        );
    }

    return (
        <div className="min-h-screen" data-testid="author-detail-page">
            {/* Header */}
            <header className="text-center py-12 md:py-16">
                <h1 className="text-4xl md:text-5xl italic mb-8">
                    {author.name}
                </h1>
                <div className="w-full max-w-3xl mx-auto h-px bg-foreground/30" />
            </header>

            {/* Author Info */}
            <section className="max-w-3xl mx-auto px-6 pb-12 text-center">
                {author.portrait_url && (
                    <img 
                        src={author.portrait_url} 
                        alt={author.name}
                        className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover mx-auto mb-6 border border-foreground/20"
                    />
                )}
                {author.bio && (
                    <p className="text-lg text-muted-foreground italic max-w-xl mx-auto">
                        {author.bio}
                    </p>
                )}
            </section>

            {/* Author's Stories */}
            {stories.length > 0 && (
                <section className="max-w-5xl mx-auto px-6 pb-24">
                    <h2 className="text-2xl md:text-3xl italic text-center mb-12">
                        Povídky
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {stories.map((story) => (
                            <Link 
                                key={story.id}
                                to={`/povidka/${story.id}`}
                                className="block group"
                                data-testid={`author-story-${story.id}`}
                            >
                                {/* Title above illustration */}
                                <h3 className="text-lg uppercase tracking-wider mb-2">
                                    {story.title}
                                </h3>
                                <p className="text-sm text-muted-foreground italic mb-4">
                                    {author.name}
                                </p>
                                
                                {/* Small square illustration */}
                                {story.cover_image_url && (
                                    <div className="w-32 h-32 overflow-hidden">
                                        <img 
                                            src={story.cover_image_url} 
                                            alt={story.title}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                            loading="lazy"
                                        />
                                    </div>
                                )}
                            </Link>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}
